/**
 * WebSocket Context — thin wrapper around DryContext WebSocket + DryNotify.
 * Maintains backward compatibility with useWebSocket() hook.
 * WebSocket lifecycle is managed by DryProvider. Notifications go through DryNotify.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useDry } from '../dry/DryContext';
import { useNotifications as useDryNotifications } from '@drystuff/ui';
import { showToast } from '../utils/toast';
import { Notification, NotificationType, NotificationPriority } from '../types';

interface WebSocketContextType {
  // Kept as `any` for backward compat with consumers that read `.socket`;
  // most reads are status checks, which now go via `isConnected` instead.
  // The underlying transport is a DryConnect WebSocketClient, not a raw
  // WebSocket — typed access via wsClient from useDry() is preferred.
  socket: any;
  isConnected: boolean;
  notifications: Notification[];
  unreadCount: number;
  sendEvent: (event: string, data: any) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { wsClient, notificationManager } = useDry();
  const [isConnected, setIsConnected] = useState(false);

  // DryNotify-backed notification state
  const dryNotif = notificationManager
    ? useDryNotifications(notificationManager)
    : { notifications: [], unreadCount: 0, markRead: async (_id: string) => {}, markAllRead: async () => {}, add: async (_input: any) => ({} as any), dismiss: async (_id: string) => {} };

  // Map DryNotify notifications to the app's Notification type for backward compat
  const notifications: Notification[] = (dryNotif.notifications || []).map((n: any) => ({
    id: n.id,
    tenant_id: n.tenant_id || '',
    user_id: n.user_id || '',
    type: (n.type || 'info') as NotificationType,
    title: n.title,
    message: n.body || '',
    priority: (n.metadata?.priority || 'normal') as NotificationPriority,
    is_read: n.read,
    created_at: n.createdAt ? new Date(n.createdAt).toISOString() : new Date().toISOString(),
    metadata: n.metadata || {},
  }));

  const unreadCount = dryNotif.unreadCount ?? 0;

  // Wire the DryConnect WebSocket from DryContext.
  //
  // The new (post-update) wsClient exposes a typed surface:
  //   - addStatusListener(cb)  — emits 'connecting' | 'connected' |
  //                              'disconnected' | 'reconnecting'
  //   - on(type, listener)     — type-based message subscription
  //                              (matches data.event field on incoming
  //                              { event, data } envelopes)
  //   - send(payload)          — queued if disconnected, flushed on
  //                              reconnect (sendQueueSize: 50)
  //
  // No more reaching for the native WebSocket; auth-aware reconnect,
  // jitter, and send queue are handled by DryConnect.
  useEffect(() => {
    if (!wsClient || !isAuthenticated || !user) {
      setIsConnected(false);
      return;
    }

    // Initial sync: status may already be 'connected' if the socket
    // opened before this provider mounted.
    setIsConnected(wsClient.status === 'connected');

    const offStatus = wsClient.addStatusListener?.((s: string) => {
      setIsConnected(s === 'connected');
    });

    // DryConnect's `on(type, listener)` matches against `data.type` by
    // default, but our backend emits `{ event, data }`. Use onMessage
    // for the full envelope and dispatch on `event` ourselves.
    const offMessage = wsClient.onMessage?.((message: any) => {
      switch (message?.event) {
        case 'notification:new':
          handleNotificationNew(message.data);
          break;
        case 'notification:update':
          if (message.data?.id && notificationManager) {
            notificationManager.markRead(message.data.id).catch(() => {});
          }
          break;
        case 'notification:delete':
          if (message.data && notificationManager) {
            notificationManager.dismiss(
              typeof message.data === 'string' ? message.data : message.data.id,
            ).catch(() => {});
          }
          break;
        case 'alert:new':
          handleAlertNew(message.data);
          break;
        case 'system:reload':
          showToast('System update available. Please refresh the page.', 'info');
          break;
        case 'system:maintenance':
          showToast(message.data || 'System maintenance scheduled', 'warning');
          break;
        default:
          // Unknown event — no-op (query sync handles data events)
          break;
      }
    });

    return () => {
      offStatus?.();
      offMessage?.();
    };
  }, [wsClient, isAuthenticated, user]);

  // ─── Message handlers ───

  const handleNotificationNew = useCallback((data: any) => {
    if (!notificationManager || !data) return;

    // Check for alert sub-type
    if (data.type === 'alert' || data.category === 'admin_alert') {
      handleAlertNew(data);
      return;
    }

    // Support-ticket activity: live-refresh any open support console/detail view.
    // Falls through so it still lands in the bell — agents want both.
    if (data.category === 'support') {
      try { window.dispatchEvent(new CustomEvent('support:refresh', { detail: data })); } catch { /* ignore */ }
    }

    // Print-queue activity: invalidate the count/list badges in the Header.
    if (data.category === 'print_queue') {
      try { window.dispatchEvent(new CustomEvent('print-queue:refresh', { detail: data })); } catch { /* ignore */ }
    }

    notificationManager.add({
      title: data.title || 'Notification',
      body: data.message || data.body || '',
      type: data.type || 'info',
      metadata: {
        priority: data.priority || 'normal',
        originalId: data.id,
        ...data.data,
      },
    }).catch(() => {});

    // Show toast for high-priority
    if (data.priority === 'high' || data.priority === 'urgent') {
      showToast(data.title, (data.type as 'success' | 'error' | 'warning' | 'info') || 'info');
    }
  }, [notificationManager]);

  const handleAlertNew = useCallback((alertData: any) => {
    if (!notificationManager || !alertData) return;

    const severity = alertData.data?.severity || alertData.severity || 'info';
    let toastType: 'success' | 'error' | 'warning' | 'info' = 'info';
    if (severity === 'critical' || severity === 'error') toastType = 'error';
    else if (severity === 'warning') toastType = 'warning';

    const title = alertData.title || alertData.data?.title || 'New Alert';
    const message = alertData.message || alertData.data?.message || '';

    showToast(
      `${severity.toUpperCase()}: ${title}${message ? ` - ${message.substring(0, 100)}` : ''}`,
      toastType,
    );

    notificationManager.add({
      title,
      body: message,
      type: 'alert',
      metadata: {
        priority: severity === 'critical' ? 'urgent' : severity === 'error' ? 'high' : 'normal',
        alertId: alertData.id || alertData.data?.alert_id,
        ...alertData.data,
      },
    }).catch(() => {});
  }, [notificationManager]);

  const sendEvent = useCallback((event: string, data: any) => {
    if (!wsClient) {
      console.warn('[WebSocketProvider] Cannot send event, no client');
      return;
    }
    // wsClient.send returns true if sent, false if queued for later
    // (sendQueueSize: 50 buffers up to 50 outbound messages while
    // disconnected and flushes them on reconnect).
    wsClient.send({ action: event, payload: data });
  }, [wsClient]);

  const markAsRead = useCallback((notificationId: string) => {
    sendEvent('notification:mark-read', { notificationId });
    dryNotif.markRead(notificationId);
  }, [sendEvent, dryNotif.markRead]);

  const markAllAsRead = useCallback(() => {
    sendEvent('notification:mark-all-read', {});
    dryNotif.markAllRead();
  }, [sendEvent, dryNotif.markAllRead]);

  const value: WebSocketContextType = {
    socket: wsClient ?? null,
    isConnected,
    notifications,
    unreadCount,
    sendEvent,
    markAsRead,
    markAllAsRead,
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};

// Re-export notification hooks for backward compat (now backed by DryNotify)
export const useNotifications = () => {
  const ctx = useContext(WebSocketContext);
  return ctx?.notifications || [];
};
export const useUnreadCount = () => {
  const ctx = useContext(WebSocketContext);
  return ctx?.unreadCount || 0;
};
export const useIsConnected = () => {
  const ctx = useContext(WebSocketContext);
  return ctx?.isConnected || false;
};
// Legacy exports that were from notificationStore — provide no-ops for preferences
export const useNotificationPreferences = () => ({
  enablePush: true,
  enableEmail: true,
  enableInApp: true,
  soundEnabled: true,
  enableSubmissions: true,
  enableApprovals: true,
  enableMaintenance: true,
  enableSystem: true,
});
export const useNotificationActions = () => {
  const ctx = useContext(WebSocketContext);
  return {
    addNotification: () => {},
    updateNotification: () => {},
    removeNotification: () => {},
    markAsRead: ctx?.markAsRead || ((_id: string) => {}),
    markAllAsRead: ctx?.markAllAsRead || (() => {}),
    clearAllNotifications: () => {},
    setNotifications: () => {},
    setConnected: () => {},
  };
};
